import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Injector, OnDestroy, OnInit, QueryList, ViewChildren } from "@angular/core";
import { FeatureName } from "@common/ADAPT.Common.Model/embed/feature-name.enum";
import { ProgressCategory } from "@common/ADAPT.Common.Model/organisation/progress-category";
import { ProgressItem } from "@common/ADAPT.Common.Model/organisation/progress-item";
import { ProgressItemStatusMetadata } from "@common/ADAPT.Common.Model/organisation/progress-item-status";
import { ProgressProduct } from "@common/ADAPT.Common.Model/organisation/progress-product";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { ErrorHandlingUtilities } from "@common/lib/utilities/error-handling-utilities";
import { SortUtilities } from "@common/lib/utilities/sort-utilities";
import { EditProgressCategoryComponent } from "@common/progress/edit-progress-category/edit-progress-category.component";
import { ImportProgressProductsDialogComponent } from "@common/progress/import-progress-products-dialog/import-progress-products-dialog.component";
import { ProgressService } from "@common/progress/progress.service";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { BaseRoutedComponent } from "@common/ux/base-routed.component";
import { ChartUtils } from "@common/ux/base-ui.service/chart-utils";
import { IDxChartCustomizePoint, IDxChartTooltipPointInfo } from "@common/ux/dx.types";
import { AuthorisationService } from "@org-common/lib/authorisation/authorisation.service";
import { JourneyProgressAuthService } from "@org-common/lib/journey-progress/journey-progress-auth.service";
import { OrganisationService } from "@org-common/lib/organisation/organisation.service";
import { OrganisationPageRouteBuilder } from "@org-common/lib/route/organisation-page-route-builder";
import { ReorderEvent } from "devextreme/ui/sortable";
import { DoneEvent } from "devextreme/viz/pie_chart";
import { DxPieChartComponent } from "devextreme-angular";
import { catchError, defer, EMPTY, filter, from, lastValueFrom, merge, switchMap, tap } from "rxjs";
import { debounceTime } from "rxjs/operators";

enum CategoryDisplayMode {
    All = "Expand all",
    Done = "Collapse all",
    None = "Collapse done",
}

interface IChartData {
    label: string;
    color: string;
    count: number;
    percent: number;
    itemCount: number;
}

interface IGroupedChartData {
    name: string;
    data: IChartData[];
}

@Component({
    selector: "adapt-journey-progress-page",
    standalone: false,
    templateUrl: "./journey-progress-page.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: "./journey-progress-page.component.scss",
})
export class JourneyProgressPageComponent extends BaseRoutedComponent implements OnInit, OnDestroy, AfterViewInit {
    public progressProducts: ProgressProduct[] = [];
    public categoryChartData: IGroupedChartData[] = [];

    public canEdit = false;
    public isEditing = false;
    public errorMessage?: string;
    public readonly CategoryDisplayMode = CategoryDisplayMode;
    public categoryDisplayMode = CategoryDisplayMode.Done;

    @ViewChildren(EditProgressCategoryComponent) private categoryComponents?: QueryList<EditProgressCategoryComponent>;

    @ViewChildren(DxPieChartComponent) private pieCharts!: QueryList<DxPieChartComponent>;

    constructor(
        injector: Injector,
        elementRef: ElementRef,
        rxjsBreezeService: RxjsBreezeService,
        private authorisationService: AuthorisationService,
        private organisationService: OrganisationService,
        private commonDataService: CommonDataService,
        private dialogService: AdaptCommonDialogService,
        private progressService: ProgressService,
    ) {
        super(injector, elementRef);

        rxjsBreezeService.breezeEntityChanged$.pipe(
            filter((c) => c.entity instanceof ProgressItem),
            debounceTime(100),
            tap(() => this.categoryChartData = this.getCategoryChartData()),
            this.takeUntilDestroyed(),
        ).subscribe();

        merge(
            rxjsBreezeService.entityTypeChanged(ProgressProduct),
            rxjsBreezeService.entityTypeChanged(ProgressCategory),
        ).pipe(
            debounceTime(100),
            switchMap(() => this.updateData()),
            this.takeUntilDestroyed(),
        ).subscribe();

        authorisationService.getHasAccess(JourneyProgressAuthService.EditJourneyProgress).pipe(
            this.takeUntilDestroyed(),
        ).subscribe((canEdit) => this.canEdit = canEdit);
    }

    public get canEditProduct() {
        return this.authorisationService.hasActiveCoachConnection() || this.authorisationService.isStakeholderManager();
    }

    public get showCodes() {
        return this.authorisationService.isStakeholderManager();
    }

    public get hasChanges() {
        return this.commonDataService.hasChanges() || this.commonDataService.saveInProgress;
    }

    public get hasCategories() {
        return this.progressProducts.some((product) => product.progressCategories.length > 0);
    }

    public cycleCategoryDisplay() {
        this.categoryDisplayMode = this.categoryDisplayMode === CategoryDisplayMode.None ? CategoryDisplayMode.Done
            : this.categoryDisplayMode === CategoryDisplayMode.Done ? CategoryDisplayMode.All : CategoryDisplayMode.None;

        // "Collapse done" state is skipped if no completed categories
        if (this.categoryDisplayMode === CategoryDisplayMode.None
            && !this.categoryComponents?.some((category) => category.isComplete)) {
            this.categoryDisplayMode = CategoryDisplayMode.Done;
        }

        this.categoryComponents?.forEach((category) => {
            category.collapsed = this.categoryDisplayMode === CategoryDisplayMode.All
                || this.categoryDisplayMode === CategoryDisplayMode.Done && category.isComplete;
        });
    }

    public onProductReorder(event: ReorderEvent) {
        this.reorder(this.progressProducts, event);
    }

    public onCategoryReorder(event: ReorderEvent, product: ProgressProduct) {
        this.reorder(product.progressCategories, event);
    }

    @Autobind
    public createProduct() {
        return this.progressService.createNewProgressProduct(this.progressProducts, this.organisationService.getOrganisationId()).pipe(
            switchMap(async (product) => {
                await this.updateData();
                ProgressService.scrollToNewProduct(this.elementRef?.nativeElement, product.progressProductId);
            }),
        );
    }

    @Autobind
    public editProduct(product: ProgressProduct) {
        return this.progressService.editProgressProduct(product, this.progressProducts).pipe(
            switchMap(() => this.updateData()),
        );
    }

    @Autobind
    public deleteProduct(product: ProgressProduct) {
        return this.progressService.deleteProduct(product, this.progressProducts).pipe(
            switchMap(() => this.updateData()),
        );
    }

    @Autobind
    public createCategory(product: ProgressProduct) {
        return this.progressService.createNewProgressCategory(product).pipe(
            switchMap(async (category) => {
                await this.updateData();
                ProgressService.scrollToNewCategory(this.elementRef?.nativeElement, category.progressCategoryId);
            }),
        );
    }

    public editCategory(category: ProgressCategory) {
        return this.progressService.editProgressCategory(category).pipe(
            switchMap(() => this.updateData()),
        );
    }

    public deleteCategory(category: ProgressCategory) {
        return this.progressService.deleteCategory(category).pipe(
            switchMap(() => this.updateData()),
        );
    }

    @Autobind
    public importProducts() {
        return defer(() => from(this.organisationService.promiseToGetOrganisation())).pipe(
            filter((organisation) => !!organisation),
            switchMap((organisation) => this.dialogService.open(ImportProgressProductsDialogComponent, { organisation })),
            switchMap(() => this.updateData()),
        );
    }

    public async ngOnInit() {
        this.commonDataService.disableSaveOverlayForEntityTypes(ProgressItem);

        await this.updateData();

        this.notifyActivated();
    }

    public ngOnDestroy() {
        this.commonDataService.enableSaveOverlayForEntityTypes(ProgressItem);
        super.ngOnDestroy();
    }

    public ngAfterViewInit() {
        // refresh the pie charts after view init, otherwise they don't show up after navigation (when using sizeGroup)
        setTimeout(() => this.pieCharts.forEach((chart) => chart.instance.refresh()));
    }

    public async updateData() {
        this.progressProducts = await lastValueFrom(this.progressService.getProgressProducts(this.organisationService.getOrganisationId()));
        this.progressProducts.forEach((p) => p.progressCategories.sort(SortUtilities.getSortByFieldFunction<ProgressCategory>("ordinal")));

        this.categoryChartData = this.getCategoryChartData();
    }

    public customiseChartPoint(point: IDxChartCustomizePoint) {
        if (point.data.color) {
            return { color: point.data.color };
        } else {
            return {};
        }
    }

    @Autobind
    public customiseChartTooltip(info: IDxChartTooltipPointInfo) {
        const data = info.point.data as IChartData;
        return {
            text: `${data.label}: ${data.count} / ${data.itemCount}`,
        };
    }

    @Autobind
    public customizeLabel(info: IDxChartTooltipPointInfo) {
        const data = info.point.data as IChartData;
        // don't show a percent for To do, misleading when no tasks done but it says 100%
        return data.label !== ProgressItemStatusMetadata.ToDo.name
            ? info.percentText
            : ``;
    }

    public updateChartDimensionAndSubscribeSizeChange(e: DoneEvent) {
        ChartUtils.updateChartDimension(e);
        this.sizeChange$.pipe(
            this.takeUntilDestroyed(),
        ).subscribe(() => ChartUtils.updateChartDimension(e));
    }

    private reorder<T extends ProgressProduct | ProgressCategory>(entities: T[], event: ReorderEvent) {
        if (!this.canEditProduct || !this.isEditing || event.fromIndex === undefined || event.toIndex === undefined || event.fromIndex === event.toIndex) {
            return;
        }

        this.errorMessage = undefined;
        this.progressService.reorderProgressEntities(entities, event.fromIndex, event.toIndex).pipe(
            switchMap(() => this.updateData()),
            catchError((error) => {
                this.errorMessage = ErrorHandlingUtilities.getHttpResponseMessage(error);
                return EMPTY;
            }),
            this.takeUntilDestroyed(),
        ).subscribe();
    }

    private getCategoryChartData() {
        const groupedChartData: IGroupedChartData[] = [];

        const categories = [{
            name: "Overall",
            items: this.progressProducts.flatMap((p) => p.progressCategories.flatMap((c) => c.progressItems)),
            product: undefined as ProgressProduct | undefined,
        }];

        // if there's only one category, we can show just the overall progress
        if (this.progressProducts.length > 1) {
            categories.push(...this.progressProducts.sort(SortUtilities.getSortByFieldFunction<ProgressProduct>("ordinal"))
                .map((product) => ({
                    name: product.name,
                    items: product.progressCategories.flatMap((c) => c.progressItems),
                    product,
                })));
        }

        for (const category of categories) {
            const itemCount = category.items.length;
            if (itemCount > 0) {
                const data: IChartData[] = ProgressItemStatusMetadata.All.reduce<IChartData[]>((chartData, progressItemStatus) => {
                    const count = category.items.filter((i) => i.status === progressItemStatus.status).length;
                    if (count > 0) {
                        chartData.push({
                            label: progressItemStatus.name,
                            color: progressItemStatus.colour,
                            count,
                            percent: count / itemCount,
                            itemCount,
                        });
                    }
                    return chartData;
                }, []);
                groupedChartData.push({ name: category.name, data });
            }
        }

        return groupedChartData;
    }
}

export const journeyProgressPageRoute = new OrganisationPageRouteBuilder<{ teamId: number }>()
    .usingNgComponent("adapt-journey-progress-page", JourneyProgressPageComponent)
    .atOrganisationUrl("/leadership/progress")
    .withTitle("Journey Progress")
    .verifyingFeatures(FeatureName.Progress)
    .verifyingAccess(JourneyProgressAuthService.ReadJourneyProgress)
    .build();
